import BomArrow from "@assets/images/bom-arrow.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { ITEM_STATUS } from "@saleor/createOrChangeNotRefContract/constants";
import { NotRefOrderSortField } from "@saleor/createOrChangeNotRefContract/urls";
import { PreviewDomesticOrderQuery } from "@saleor/graphql";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React, { useRef } from "react";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

export interface IPreviewOrderNotRefTable
  extends ListProps,
    SortPage<NotRefOrderSortField> {
  previewOrderLines: PreviewDomesticOrderQuery["previewDomesticOrder"]["previewItemData"];
  settings: any;

  onUpdateListSettings: any;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
}

const PreviewOrderNotRefTable: React.FC<IPreviewOrderNotRefTable> = props => {
  const {
    previewOrderLines,
    settings,
    pageInfo,
    disabled,
    onPreviousPage,
    onNextPage,
    onNextLastPage,
    onPreviousFirstPage,
    onUpdateListSettings,
    latestPageItemNumber,
    totalCount,
    sort,
    onSort
  } = props;

  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <div className={classes.root} ref={containerRef}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader
                direction={
                  sort.sort === NotRefOrderSortField.itemNo
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(NotRefOrderSortField?.itemNo)}
                textAlign="left"
                className={classes.col100}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({
                    defaultMessage: "Item No."
                  })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="left" className={classes.col190}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Material Code" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="left" className={classes.col340}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "รายการสินค้า" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign={"center"}
                className={classes.col160}
                option="หน่วย"
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "จำนวน" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign={"center"}
                className={classes.col160}
                option="หน่วย"
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "น้ำหนัก" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="right" className={classes.col120}>
                <MyTypography
                  variant="subtitle3"
                  style={{ textAlign: "right" }}
                >
                  {intl.formatMessage({ defaultMessage: "ราคาต่อหน่วย" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="right" className={classes.col120}>
                <MyTypography
                  variant="subtitle3"
                  style={{ textAlign: "right" }}
                >
                  {intl.formatMessage({ defaultMessage: "ราคารวมสุทธิ" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="center" className={classes.col120}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Request Date" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={classNames(classes.col120)}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Confirm Date" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="center" className={classes.col120}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Delivery Plant" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="center" className={classes.col120}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Confirm Plant" })}
                </MyTypography>
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(previewOrderLines, (item, index) => (
              <TableRow
                className={
                  item?.itemStatusEn === ITEM_STATUS.CANCEL
                    ? classes.rowDisable
                    : classes.rowClickable
                }
                key={index}
              >
                <TableCell align="left">
                  <MyTypography
                    variant={
                      item.parentItemNo !== "" && item.bomFlag === true
                        ? null
                        : "subtitle3"
                    }
                    className={classes?.itemNo}
                  >
                    {item?.itemNo}
                  </MyTypography>
                </TableCell>
                <TableCell align="left">
                  <MyTypography
                    variant={
                      item.parentItemNo !== "" && item.bomFlag === true
                        ? null
                        : "subtitle3"
                    }
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {item.parentItemNo !== "" && item.bomFlag === true ? (
                      <InlineSVG
                        className={
                          getArrowDirection(sort.asc) === "asc"
                            ? ""
                            : classes?.flip
                        }
                        src={BomArrow}
                        width={24}
                        height={24}
                        style={{ marginRight: "10px" }}
                      />
                    ) : null}
                    {item?.materialCode}
                  </MyTypography>
                </TableCell>
                <TableCell align="left">
                  <MyTypography
                    variant={
                      item.parentItemNo !== "" && item.bomFlag === true
                        ? null
                        : "subtitle3"
                    }
                  >
                    <span>
                      {maybe(() => item?.materialDescription)}
                      <MyTypography
                        variant={"caption"}
                        className={classNames(
                          {
                            [classes.boxCancel]:
                              item?.itemStatusEn === ITEM_STATUS.CANCEL
                          },
                          "canceled"
                        )}
                        style={{ width: "auto", display: "block" }}
                      >
                        {item?.itemStatusEn === ITEM_STATUS.CANCEL && (
                          <Typography variant="caption">
                            <div>Cancelled (93)</div>
                          </Typography>
                        )}
                      </MyTypography>
                    </span>
                  </MyTypography>
                </TableCell>
                <TableCell align="right" className={classes.colMin160}>
                  <span className={classes.colQtyLeft}>
                    <DecimalFormat
                      value={item?.quantity}
                      isGrayDecimal={false}
                      countDecimal={
                        item.salesUnit === "KG" || item.salesUnit === "TON"
                          ? 3
                          : 0
                      }
                    />
                  </span>
                  <span className={classes.colQtyRite}>
                    {maybe(() => item?.salesUnit)}
                  </span>
                </TableCell>
                <TableCell align="right" className={classes.colMin160}>
                  <span className={classes.colQtyLeft}>
                    <DecimalFormat
                      value={item?.weight}
                      isGrayDecimal={false}
                      countDecimal={3}
                    />
                  </span>
                  <span className={classes.colQtyRite}>
                    {maybe(() => item?.weightUnit)}
                  </span>
                </TableCell>
                <TableCell align="right">
                  {item.parentItemNo !== "" && item.bomFlag === true ? (
                    "-"
                  ) : (
                    <DecimalFormat
                      value={item?.pricePerUnit}
                      isGrayDecimal={false}
                      countDecimal={2}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.parentItemNo !== "" && item.bomFlag === true ? (
                    "-"
                  ) : (
                    <DecimalFormat
                      value={item?.netPrice}
                      isGrayDecimal={false}
                      countDecimal={2}
                    />
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  className={
                    item?.requestDate &&
                    item?.confirmedDate &&
                    item?.requestDate !== item?.confirmedDate
                      ? classes.estDateNotMatch
                      : classes.estDate
                  }
                >
                  {item.parentItemNo !== "" && item.bomFlag === true
                    ? "-"
                    : maybe(() => item?.requestDate)}
                </TableCell>
                <TableCell
                  align="center"
                  className={
                    item?.confirmedDate &&
                    item?.requestDate &&
                    item?.confirmedDate !== item?.requestDate
                      ? classes.estDateNotMatch
                      : classes.estDate
                  }
                >
                  {item.parentItemNo !== "" && item.bomFlag === true
                    ? "-"
                    : maybe(() => item?.confirmedDate)}
                </TableCell>
                <TableCell
                  align="center"
                  className={
                    item?.plant &&
                    item?.confirmedPlant &&
                    item?.plant !== item?.confirmedPlant
                      ? classes.estDateNotMatch
                      : classes.estDate
                  }
                >
                  {item.parentItemNo !== "" && item.bomFlag === true
                    ? "-"
                    : maybe(() => item?.plant)}
                </TableCell>
                <TableCell
                  align="center"
                  className={
                    item?.confirmedPlant &&
                    item?.plant &&
                    item?.confirmedPlant !== item?.plant
                      ? classes.estDateNotMatch
                      : classes.estDate
                  }
                >
                  {item.parentItemNo !== "" && item.bomFlag === true
                    ? "-"
                    : maybe(() => item?.confirmedPlant)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <StickyHorizontalScrollBar container={containerRef} bottom={160} />
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          disabled={disabled}
          isBackFistPage
          isNextLastPage
          selectColor="#007E92"
          rowNumberSet={BIG_ROW_NUMBER_SET}
        />
      </Box>
    </>
  );
};
export default PreviewOrderNotRefTable;
